/**
 * Registro dos renderers que o lado legado empresta ao motor novo.
 *
 * A direção da dependência é o ponto: **legado → motor**, nunca o contrário. O
 * `screen-engine` publica o token `NODE_RENDERERS` e um mapa nativo; quem tem
 * componente antigo para emprestar sobrepõe o tipo aqui.
 *
 * Sem essa inversão, o motor precisaria importar `DataGridComponent` — e a Fase 8
 * (apagar `builder-screen-beta/` inteiro) ficaria impossível, porque o motor
 * dependeria do que se quer apagar.
 *
 * Conforme a Fase 6 for movendo componente por componente para
 * `screen-engine/components/`, cada linha daqui some e o mapa nativo passa a
 * apontar direto. Quando a lista de empréstimos ficar vazia, a ponte acabou.
 *
 * ---
 *
 * **Por que o mapa é montado numa FUNÇÃO e não numa constante**
 *
 * A primeira versão fazia `export const RENDERERS_WITH_LEGACY = { ...BUILTIN_RENDERERS }`
 * no corpo do módulo, e isso quebrou com
 * `ReferenceError: Cannot access 'BUILTIN_RENDERERS' before initialization`.
 *
 * É a mesma armadilha de valor↔valor que apareceu na Fase 0 entre
 * `file-loader.component` e `FileLoaderServices`: um `const` no corpo do módulo é
 * avaliado na CARGA, e este arquivo participa de um ciclo — o mapa nativo puxa o
 * renderer da ponte do webhook, que puxa o `WebhookCreatorModule`, que puxa
 * serviços do `builder-screen-beta`. Dependendo de por qual arquivo o bundle
 * entra no ciclo, `BUILTIN_RENDERERS` ainda está na zona morta temporal.
 *
 * `useFactory` resolve por construção: a fábrica roda quando o injetor cria o
 * valor, muito depois de todos os módulos terem terminado de carregar. O ciclo
 * continua existindo — desfazê-lo é trabalho da Fase 6, quando o `data-grid`
 * mudar de pasta — mas deixa de ser um problema de ordem de inicialização.
 */
import { Provider, Type } from '@angular/core';

import { NODE_RENDERERS, NodeRendererMap } from '../../screen-engine/render/node-renderer';
import { BUILTIN_RENDERERS } from '../../screen-engine/render/renderer-map';
import { DataGridRenderer } from './data-grid.renderer';

/**
 * O mapa nativo, com os tipos que o módulo antigo ainda desenha melhor.
 *
 * Função, não constante — ver o comentário acima.
 */
export function renderersWithLegacy(): NodeRendererMap {
  // A LISTA DE EMPRESTIMOS ESTA VAZIA.
  //
  // O `datagrid` era o unico, e saiu na Fase 6: o motor tem a propria copia em
  // `screen-engine/components/data-grid`, standalone e OnPush. O renderer daqui
  // continua no lugar, parado, servindo o canvas antigo ate a Fase 8.
  //
  // Este provider deixou de ser necessario e sera removido junto com o modulo.
  // Fica enquanto o modulo fica, porque tirar um provider que ninguem mais usa
  // nao vale um commit a mais no meio da migracao.
  return { ...BUILTIN_RENDERERS };
}

/**
 * Provider para quem renderiza tela dentro do contexto legado.
 *
 * Vai no `providers` do componente que hospeda `<tem-screen-view>` — não em root.
 * Assim, quem renderizar tela fora deste módulo (a Fase 4, o launcher depois da
 * Fase 6) recebe o mapa nativo, sem o legado junto.
 */
export const LEGACY_RENDERERS_PROVIDER: Provider = {
  provide: NODE_RENDERERS,
  useFactory: renderersWithLegacy
};
